import { Pager } from "@/app/Pager";
import { useState } from "react";
import { useCanWrite } from "@/app/auth";
import { AppendDrawer } from "@/app/AppendDrawer";
import { accounts, customers, CHARGE_ON, sellPrices, type Account, type Customer, type Input, type SellPrice } from "@/app/master";
import { loadingOf, nameOf, useList } from "@/app/queries";
import { usePage } from "@/app/usePage";
import { Alert, Button, EmptyState, formatMoney, formatTime, PageHeader, Pill, Table, TableWrap, Tabs, Th } from "@/ui";
import { Navigate } from "react-router-dom";
import { listedPrices, usePriceClock } from "./priceList";
import { CostPrices } from "./CostPrices";
import { PriceForm } from "./PriceForm";
import { priceDraft, priceInput } from "./priceDraft";

import { PriceFilters } from "./PriceFilters";
import { EMPTY_PRICE_FILTERS, matchesPriceFilters } from "./priceFilterState";

type Tab = "sell" | "cost";

/** 各页签的新增动作文案。 */
const ADD_LABEL: Partial<Record<Tab, string>> = { sell: "新增客户价格", cost: "新增通道价格" };

/** 价格列表展示当前期间；详情编辑时间线，历史同步任务在独立的“价格同步”页面查看（D113）。 */
export function Prices() {
  if (new URLSearchParams(window.location.search).get("tab") === "tasks") return <Navigate to="/price-sync-tasks" replace />;
  return <PriceConfiguration />;
}

function PriceConfiguration() {
  const canWrite = useCanWrite(); // 只读账号不显示写操作，服务端同样拒绝（docs/04 第 9 节）
  const [tab, setTab] = useState<Tab>("sell");
  const [filters, setFilters] = useState(EMPTY_PRICE_FILTERS);
  const accountId = filters.entityId;
  const [adding, setAdding] = useState(false);
  const now = usePriceClock();
  const customerList = useList("customers", customers);
  const accountList = useList("accounts", accounts);
  const priceList = useList("sell-prices", sellPrices, accountId ? { account_id: accountId } : undefined);
  const rows = listedPrices(priceList.data ?? [], (p) => p.account_id, now).filter((p) => matchesPriceFilters(filters, p, p.account_id, accountList.data?.find((a) => a.id === p.account_id)?.customer_id));
  const paged = usePage("sell-prices", rows, [filters]);
  const addLabel = ADD_LABEL[tab];

  return (
    <>
      <PageHeader title="价格配置" description="列表显示当前生效价格，没有时显示最近将生效的一条；改动涉及历史时自动排队同步明细、报表与余额。"
        actions={canWrite && addLabel && <Button variant="primary" onClick={() => setAdding(true)}>{addLabel}</Button>} />
      <Tabs items={[{ key: "sell", label: "客户价格" }, { key: "cost", label: "通道价格" }]} value={tab} onChange={(k) => { setTab(k); setAdding(false); }} />
      <div style={{ height: "var(--space-4)" }} />
      {tab === "sell" && (
        <>
          {priceList.error && <Alert tone="danger" title="加载失败">{priceList.error.message}</Alert>}
          <PriceFilters ownerLabel="客户" entityLabel="账号" entitySearch="搜索账号名称或 system_id"
            owners={customerList.data} entities={accountList.data?.map((a) => ({ ...a, ownerId: a.customer_id }))}
            value={filters} onChange={setFilters} />
          <TableWrap loading={loadingOf(priceList)}>
            {rows.length ? (
              <>
                <Table>
                  <thead><tr><Th w="id">ID</Th><Th w="name">账号</Th><Th w="name">客户</Th><Th w="code">MCC</Th><Th w="code">MNC</Th><Th numeric w="money">价格</Th><Th w={96}>计费类型</Th><Th numeric w={224}>生效时间</Th><Th w={120}>操作者</Th><Th /></tr></thead>
                  <tbody>
                    {paged.rows.map((p) => (
                      <tr key={p.id}>
                        <td className="ax-mono">{p.id}</td><td>{nameOf(accountList.data, p.account_id)}</td><td>{nameOf(customerList.data, accountList.data?.find((a) => a.id === p.account_id)?.customer_id ?? 0)}</td><td className="ax-mono">{p.mcc}</td><td className="ax-mono">{p.mnc ?? "全部"}</td>
                        <td className="ax-num"><span className="ax-money">{formatMoney(p.price, p.currency, 6)}</span></td>
                        <td>{CHARGE_ON[p.charge_on]}</td><td className="ax-num ax-mono">{formatTime(p.effective_from, false)}{Date.parse(p.effective_from) > now && <> <Pill tone="neutral">待生效</Pill></>}</td><td className="ax-secondary">{p.created_by}</td>
                        <td><a className="ax-link" href={`/prices/sell/${p.id}`}>{canWrite ? "编辑" : "详情"}</a></td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                <Pager {...paged} />
              </>
            ) : (
              !priceList.isPending && <EmptyState title="没有客户价格">没有客户价格的目的地无法计费，消息会被拒绝。</EmptyState>
            )}
          </TableWrap>
        </>
      )}
      {tab === "cost" && <CostPrices adding={adding} onCloseAdd={() => setAdding(false)} />}
      {adding && tab === "sell" && <PriceDrawer accountId={accountId} accounts={accountList.data} customers={customerList.data} onClose={() => setAdding(false)} />}
    </>
  );
}

function PriceDrawer({ accountId, accounts: accountRows, customers: customerRows, onClose }: { accountId: string; accounts?: Account[]; customers?: Customer[]; onClose: () => void }) {
  const customer = customerRows?.find((c) => c.id === accountRows?.find((a) => String(a.id) === accountId)?.customer_id);
  return (
    <AppendDrawer
      noun="客户价格"
      initial={priceDraft(accountId, customer?.currency, customer ? String(customer.id) : "", customer?.charge_on)}
      convert={priceInput}
      save={(body: Input<SellPrice>) => sellPrices.create(body)}
      invalidate={["sell-prices", "price-sync-tasks"]}
      message={(p) => p.sync_task_id ? `已新增客户价格，价格同步任务 #${p.sync_task_id} 已排队` : "已新增客户价格"}
      note="保存价格并发布配置；涉及历史变化时自动创建串行同步任务。此操作写入审计日志。"
      summary={(body) => [
        { label: "客户", value: nameOf(customerRows, accountRows?.find((a) => a.id === body.account_id)?.customer_id ?? 0) },
        { label: "账号", value: nameOf(accountRows, body.account_id) },
        { label: "目的地", value: `${body.mcc}-${body.mnc ?? "全部"}`, mono: true },
        { label: "价格", value: <span className="ax-money">{formatMoney(body.price, body.currency, 6)}</span> },
        { label: "计费类型", value: CHARGE_ON[body.charge_on] },
        { label: "生效时间", value: `${formatTime(body.effective_from)} 北京时间`, mono: true },
      ]}
      onClose={onClose}
    >
      {(form) => <PriceForm draft={form.draft} errors={form.errors} set={form.set} accounts={accountRows} customers={customerRows} />}
    </AppendDrawer>
  );
}
